import '@fontsource/marcellus/400.css';
import './kart.css';
import {
  ArrowLeft,
  BadgeCheck,
  CalendarRange,
  ChartLine,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
  Maximize2,
  Megaphone,
  Minus,
  Orbit,
  PenLine,
  Plus,
  Send,
  ShieldCheck,
  Waypoints,
} from 'lucide-react';
import {
  memo,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { Locale } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { prefersReducedMotion, useSize, useTween } from './hooks';
import {
  ancestors,
  angleDelta,
  DEPENDENTS,
  FAN,
  FAN_LOCAL,
  fanBounds,
  JOB_BY_ID,
  lerp,
  lerpPt,
  lineRoot,
  type Pt,
  place,
  polar,
  rng,
  SKY,
  SKY_LOCAL,
  SKY_MAX_LABEL,
  SKY_REACH,
  STAGE_COLOR,
  STAGE_INDEX,
  STAGE_SHORT,
  type StageTree,
  TREE_BY_STAGE,
  TREES,
} from './layout';
import { BUILD_ORDER, JOBS, type Job, type Level, STAGES } from './nonoMap';

/*
 * The living Nono map. Two scenes on one canvas:
 *
 *   SKY  — the overview: ten stage constellations around the Nono brain.
 *          Drag turns the wheel (with momentum and a soft snap), ⌘/ctrl-scroll
 *          or pinch zooms, ‹ › steps a stage to the front. Dots are jobs; a
 *          branch is a real in-stage dependency, and hovering a dot draws its
 *          cross-stage chain as arcs through the middle.
 *   DIVE — one stage, large: the same tree as a fan with named nodes, the
 *          stage badge at its foot. Narrow screens get the tree as a list.
 *
 * Camera = the SVG viewBox, fitted to whatever is left beside the floating
 * detail card. Every animation is either functional (morphs, dive, draw-in)
 * or ambient-and-quiet (stars, brain); prefers-reduced-motion stops them all.
 */

export interface SkyMessages {
  mapLabel: string;
  wheel: string;
  journey: string;
  back: string;
  zoomIn: string;
  zoomOut: string;
  fit: string;
  prevStage: string;
  nextStage: string;
  skyHint: string;
  skyHintLine: string;
  skyHintTouch: string;
  diveHint: string;
  diveHintTouch: string;
  hubName: string;
  openStage: (name: string, jobs: string, gaps: string) => string;
  level: Record<Level, string>;
  jobsWord: (n: number) => string;
  gapsWord: (n: number) => string;
  stageOf: (i: number, n: number) => string;
  buildFirst: string;
}

const STAGE_ICON: Record<string, LucideIcon> = {
  plan: CalendarRange,
  lage: PenLine,
  sjekke: ShieldCheck,
  godkjenne: BadgeCheck,
  publisere: Send,
  laere: ChartLine,
};

type Mode = 'wheel' | 'line';
type View = { x: number; y: number; w: number; h: number };

const TOP_INSET = 52; // keeps the toolbar clear of the content
const NARROW = 640; // below this the dive shows a list instead of a fan
const f = (n: number) => n.toFixed(1);
const RANK = new Map(BUILD_ORDER.map((id, i) => [id, i]));

/** Fit `content` into the svg box, leaving `reserveR` px free on the right. */
function fitView(
  content: View,
  box: { w: number; h: number },
  reserveR: number,
  zoom = 1,
  pan: Pt = { x: 0, y: 0 },
) {
  const availW = Math.max(240, box.w - reserveR);
  const availH = Math.max(200, box.h - TOP_INSET);
  const raw = Math.min(availW / content.w, availH / content.h);
  const fit = Number.isFinite(raw) && raw > 0 ? raw : 1;
  const s = fit * zoom;
  const cx = content.x + content.w / 2 + pan.x;
  const cy = content.y + content.h / 2 + pan.y;
  const view: View = {
    x: cx - availW / 2 / s,
    y: cy - (TOP_INSET + availH / 2) / s,
    w: box.w / s,
    h: box.h / s,
  };
  return {
    view,
    s,
    availW,
    availH,
    fit,
    cx: content.x + content.w / 2,
    cy: content.y + content.h / 2,
  };
}

const WHEEL_R = SKY_MAX_LABEL + 70;
const WHEEL_CONTENT: View = {
  x: -WHEEL_R - 60,
  y: -WHEEL_R,
  w: 2 * WHEEL_R + 120,
  h: 2 * WHEEL_R + 20,
};
const LINE_CONTENT: View = (() => {
  const xs = STAGES.map((_, i) => lineRoot(i).x);
  const ys = STAGES.map((_, i) => lineRoot(i).y);
  const x0 = Math.min(...xs) - 190;
  const x1 = Math.max(...xs) + 190;
  const y0 = Math.min(...ys) - 270;
  const y1 = Math.max(...ys) + 95;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
})();

/* ── static ambience (memoised: never re-renders) ────────────────────── */

const Stars = memo(function Stars() {
  const stars = useMemo(() => {
    const r = rng(7);
    return Array.from({ length: 150 }, (_, i) => ({
      i,
      x: r() * 100,
      y: r() * 100,
      s: 0.5 + r() * 1.1,
      o: 0.12 + r() * 0.45,
      t: 4 + r() * 6,
      d: r() * 6,
    }));
  }, []);
  return (
    <svg className="pointer-events-none absolute inset-0 size-full" aria-hidden>
      {stars.map((s) => (
        <circle
          key={s.i}
          className="kart-star"
          cx={`${s.x}%`}
          cy={`${s.y}%`}
          r={s.s}
          fill="var(--sky-ink)"
          style={{
            ['--o' as string]: s.o,
            ['--t' as string]: `${s.t}s`,
            ['--dl' as string]: `${s.d}s`,
            opacity: s.o,
          }}
        />
      ))}
    </svg>
  );
});

const Brain = memo(function Brain({ spin }: { spin: boolean }) {
  const dots = useMemo(() => {
    const r = rng(42);
    const colors = Object.values(STAGE_COLOR);
    return Array.from({ length: 190 }, (_, i) => {
      const a = r() * Math.PI * 2;
      const rad = SKY.brain * Math.sqrt(r()) * (0.25 + 0.75 * r());
      const tinted = r() < 0.28;
      return {
        i,
        x: Math.cos(a) * rad,
        y: Math.sin(a) * rad,
        s: 0.7 + r() * 2,
        o: 0.18 + r() * 0.65,
        c: tinted ? (colors[Math.floor(r() * colors.length)] ?? '#fff') : 'var(--sky-ink)',
      };
    });
  }, []);
  return (
    <g aria-hidden>
      <g>
        {dots.map((d) => (
          <circle key={d.i} cx={f(d.x)} cy={f(d.y)} r={f(d.s)} fill={d.c} opacity={d.o} />
        ))}
        {spin && (
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            dur="160s"
            repeatCount="indefinite"
          />
        )}
      </g>
      <circle r={SKY.brain + 10} fill="none" stroke="var(--sky-faint)" strokeDasharray="1 7" />
    </g>
  );
});

/* ── node glyphs (shared by sky dots, fan nodes and the list) ────────── */

function Glyph({
  level,
  cx,
  cy,
  r,
  glow,
}: {
  level: Level;
  cx: number;
  cy: number;
  r: number;
  glow?: boolean;
}) {
  const sw = Math.max(1, r * 0.16);
  switch (level) {
    case 'auto':
      return (
        <circle
          cx={f(cx)}
          cy={f(cy)}
          r={f(r)}
          fill="var(--sky-ink)"
          className={glow ? 'kart-glow' : undefined}
        />
      );
    case 'assisted':
      return (
        <circle
          cx={f(cx)}
          cy={f(cy)}
          r={f(r)}
          fill="rgba(var(--sky-ink-rgb), 0.42)"
          stroke="var(--sky-ink)"
          strokeWidth={f(sw)}
        />
      );
    case 'manual':
      return (
        <circle
          cx={f(cx)}
          cy={f(cy)}
          r={f(r)}
          fill="rgba(var(--sky-ink-rgb), 0.07)"
          stroke="rgba(var(--sky-ink-rgb), 0.75)"
          strokeWidth={f(sw)}
        />
      );
    default:
      return (
        <circle
          cx={f(cx)}
          cy={f(cy)}
          r={f(r)}
          fill="rgba(var(--sky-ink-rgb), 0.03)"
          stroke="var(--sky-bad)"
          strokeWidth={f(sw)}
          strokeDasharray={`${f(r * 0.42)} ${f(r * 0.32)}`}
        />
      );
  }
}

/** HTML twin of Glyph for legends and lists outside the canvas. */
export function GlyphDot({ level, className }: { level: Level; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block shrink-0 rounded-full',
        level === 'auto' && 'bg-fg shadow-[0_0_6px_rgb(0_0_0/0.0)]',
        level === 'assisted' && 'border-[1.5px] border-fg bg-fg/40',
        level === 'manual' && 'border-[1.5px] border-fg/75 bg-fg/5',
        level === 'missing' && 'border-[1.5px] border-dashed border-bad bg-transparent',
        className ?? 'size-2.5',
      )}
    />
  );
}

/* ── the component ───────────────────────────────────────────────────── */

export function SkyMap({
  locale,
  m,
  mode,
  onMode,
  diveStage,
  onDive,
  selected,
  hovered,
  levelFilter,
  panelPx,
  onHover,
  onSelect,
}: {
  locale: Locale;
  m: SkyMessages;
  mode: Mode;
  onMode: (m: Mode) => void;
  diveStage: string | null;
  onDive: (stageId: string | null) => void;
  selected: string | null;
  hovered: string | null;
  levelFilter: Level | null;
  panelPx: number;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null, viaKeyboard?: boolean) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const skyRef = useRef<SVGSVGElement>(null);
  const box = useSize(rootRef);
  const narrow = box.w < NARROW;
  const motion = !prefersReducedMotion();

  // scene tweens
  const e = useTween(mode === 'line' ? 1 : 0, 650); // wheel → line
  const d = useTween(diveStage ? 1 : 0, 620); // sky → dive
  const panel = useTween(panelPx > 0 ? 1 : 0, 220);
  const lastPanelPx = useRef(panelPx);
  if (panelPx > 0) lastPanelPx.current = panelPx;
  const reserve = lastPanelPx.current * panel; // eases out on close too
  const [coarse] = useState(
    () =>
      typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches),
  );

  // the dive keeps showing the last stage while it fades out
  const [shownDive, setShownDive] = useState<string | null>(diveStage);
  useEffect(() => {
    if (diveStage) setShownDive(diveStage);
  }, [diveStage]);
  useEffect(() => {
    if (!diveStage && d === 0) setShownDive(null);
  }, [diveStage, d]);

  /* wheel rotation (deg), user zoom and pan */
  const [wheel, setWheel] = useState(0);
  const wheelRef = useRef(0);
  wheelRef.current = wheel;
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState<Pt>({ x: 0, y: 0 });
  const spinRef = useRef(0); // rAF id for wheel animations

  const stopSpin = () => cancelAnimationFrame(spinRef.current);
  const animateWheelTo = useCallback(
    (to: number, ms = 520) => {
      cancelAnimationFrame(spinRef.current);
      const from = wheelRef.current;
      if (!motion) {
        setWheel(to);
        return;
      }
      const t0 = performance.now();
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / ms);
        const k = 1 - (1 - p) ** 3;
        setWheel(from + (to - from) * k);
        if (p < 1) spinRef.current = requestAnimationFrame(step);
      };
      spinRef.current = requestAnimationFrame(step);
    },
    [motion],
  );
  useEffect(() => () => cancelAnimationFrame(spinRef.current), []);

  /** The stage at the bottom of the wheel ("in front"). */
  const focusIndex = useMemo(() => {
    let best = 0;
    let bestD = 999;
    STAGES.forEach((_, i) => {
      const dd = Math.abs(angleDelta(i * SKY.step + wheel, 180));
      if (dd < bestD) {
        bestD = dd;
        best = i;
      }
    });
    return best;
  }, [wheel]);
  const frontTarget = useRef<number | null>(null);
  const bringToFront = useCallback(
    (index: number) => {
      frontTarget.current = index;
      const target = 180 - index * SKY.step;
      animateWheelTo(wheelRef.current + angleDelta(wheelRef.current, target));
    },
    [animateWheelTo],
  );

  // diving into a stage turns it to the front, so leaving the dive lands there
  useEffect(() => {
    if (diveStage && mode === 'wheel') bringToFront(STAGE_INDEX.get(diveStage) ?? 0);
  }, [diveStage, mode, bringToFront]);

  /* ── sky geometry for the current frame ── */
  const stageGeo = useMemo(
    () =>
      TREES.map((t) => {
        const ang = t.index * SKY.step + wheel;
        const rootWheel = polar(SKY.ring, ang);
        const rootLine = lineRoot(t.index);
        const root = lerpPt(rootWheel, rootLine, e);
        const rot = lerp(angleDelta(0, ang), 0, e);
        const labelWheel = polar(SKY.ring + (SKY_REACH.get(t.stage.id) ?? 120) + SKY.labelGap, ang);
        const labelLine = { x: rootLine.x, y: rootLine.y + 62 };
        // side labels grow outward, top/bottom ones stay centred — never into a neighbour
        const side = Math.sin((ang * Math.PI) / 180);
        const anchor: 'start' | 'middle' | 'end' =
          e > 0.5 || Math.abs(side) < 0.42 ? 'middle' : side > 0 ? 'start' : 'end';
        return { t, ang, root, rot, anchor, label: lerpPt(labelWheel, labelLine, e) };
      }),
    [wheel, e],
  );
  const jobPos = useMemo(() => {
    const out = new Map<string, Pt>();
    for (const g of stageGeo) {
      const local = SKY_LOCAL.get(g.t.stage.id);
      if (!local) continue;
      for (const [id, p] of local) out.set(id, place(p, g.rot, g.root));
    }
    return out;
  }, [stageGeo]);

  /* camera */
  // narrow screens are width-bound: give the outward-anchored side labels room
  const padX = narrow ? 170 : 0;
  const content: View = {
    x: lerp(WHEEL_CONTENT.x, LINE_CONTENT.x, e) - padX,
    y: lerp(WHEEL_CONTENT.y, LINE_CONTENT.y, e),
    w: lerp(WHEEL_CONTENT.w, LINE_CONTENT.w, e) + 2 * padX,
    h: lerp(WHEEL_CONTENT.h, LINE_CONTENT.h, e),
  };
  const base = fitView(content, box, reserve, zoom, pan);
  // during the dive the sky camera flies towards the stage root and fades
  const diveGeo = shownDive ? stageGeo[STAGE_INDEX.get(shownDive) ?? 0] : undefined;
  let skyView = base.view;
  let skyScale = base.s;
  if (diveGeo && d > 0) {
    const s1 = base.s * 2.8;
    const s = base.s * (s1 / base.s) ** d;
    const cx = lerp(base.view.x + base.availW / 2 / base.s, diveGeo.root.x, d);
    const cy = lerp(
      base.view.y + (TOP_INSET + (box.h - TOP_INSET) / 2) / base.s,
      diveGeo.root.y,
      d,
    );
    skyView = {
      x: cx - base.availW / 2 / s,
      y: cy - (TOP_INSET + (box.h - TOP_INSET) / 2) / s,
      w: box.w / s,
      h: box.h / s,
    };
    skyScale = s;
  }
  const px = 1 / skyScale; // one screen pixel in world units

  /** Zoom by `factor`, keeping the world point under (clientX, clientY) still. */
  const camRef = useRef({ base, box, zoom, pan });
  camRef.current = { base, box, zoom, pan };
  const zoomAt = useCallback((factor: number, clientX?: number, clientY?: number) => {
    const { base: b, zoom: z } = camRef.current;
    const z2 = Math.min(4, Math.max(0.6, z * factor));
    const rect = skyRef.current?.getBoundingClientRect();
    if (clientX === undefined || clientY === undefined || !rect) {
      setZoom(z2);
      return;
    }
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const wx = b.view.x + sx / b.s;
    const wy = b.view.y + sy / b.s;
    const s2 = b.fit * z2;
    setZoom(z2);
    // fitView: view.x = (content centre + pan.x) - availW/2/s — solve for the pan that pins (wx, wy)
    setPan({
      x: wx - sx / s2 + b.availW / 2 / s2 - b.cx,
      y: wy - sy / s2 + (TOP_INSET + b.availH / 2) / s2 - b.cy,
    });
  }, []);
  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;

  /* ── highlight sets ── */
  const active = hovered ?? (selected && selected !== 'hub' ? selected : null);
  const { up, down } = useMemo(() => {
    if (!active || !JOB_BY_ID.has(active))
      return { up: new Set<string>(), down: new Set<string>() };
    return { up: ancestors(active), down: new Set(DEPENDENTS.get(active) ?? []) };
  }, [active]);
  const filtered = (j: Job) => Boolean(levelFilter && j.level !== levelFilter);

  /* ── pointer: drag to turn / pan, pinch or ⌘-scroll to zoom ── */
  const drag = useRef<{
    id: number;
    x: number;
    y: number;
    a0: number;
    w0: number;
    pan0: Pt;
    moved: boolean;
    rotate: boolean;
    lastA: number;
    lastT: number;
    v: number;
  } | null>(null);
  const pinch = useRef<{ last: number } | null>(null);
  const pointers = useRef(new Map<number, Pt>());
  const suppressClick = useRef(false);

  const screenToWorld = (clientX: number, clientY: number): Pt | null => {
    const svg = skyRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const p = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
    return { x: p.x, y: p.y };
  };
  const wheelCenterScreen = (): Pt | null => {
    const svg = skyRef.current;
    const ctm = svg?.getScreenCTM();
    if (!ctm) return null;
    const p = new DOMPoint(0, 0).matrixTransform(ctm);
    return { x: p.x, y: p.y };
  };

  const onPointerDown = (ev: ReactPointerEvent<SVGSVGElement>) => {
    if (diveStage || ev.button !== 0) return;
    pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) pinch.current = { last: Math.hypot(a.x - b.x, a.y - b.y) };
      drag.current = null;
      return;
    }
    stopSpin();
    frontTarget.current = null;
    ev.currentTarget.setPointerCapture(ev.pointerId); // a release over the toolbar still ends the drag
    const c = wheelCenterScreen();
    const a0 = c ? Math.atan2(ev.clientY - c.y, ev.clientX - c.x) : 0;
    drag.current = {
      id: ev.pointerId,
      x: ev.clientX,
      y: ev.clientY,
      a0,
      w0: wheelRef.current,
      pan0: pan,
      moved: false,
      rotate: mode === 'wheel' && zoom <= 1.25,
      lastA: a0,
      lastT: performance.now(),
      v: 0,
    };
  };
  const onPointerMove = (ev: ReactPointerEvent<SVGSVGElement>) => {
    if (pointers.current.has(ev.pointerId))
      pointers.current.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
    if (pinch.current && pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      if (a && b) {
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        zoomAt(dist / (pinch.current.last || dist), (a.x + b.x) / 2, (a.y + b.y) / 2);
        pinch.current.last = dist;
      }
      return;
    }
    const g = drag.current;
    if (!g) {
      // hover: the nearest job owns the pointer (not whichever dot is painted last)
      if (ev.pointerType !== 'touch') {
        const t = pickTarget(ev.clientX, ev.clientY);
        const id = t?.kind === 'job' ? t.id : null;
        if (id !== lastHover.current) {
          lastHover.current = id;
          onHover(id);
        }
        const k = t ? 'pointer' : 'grab';
        if (k !== cursorKind) setCursorKind(k);
      }
      return;
    }
    if (g.id !== ev.pointerId) return;
    if (ev.pointerType === 'mouse' && ev.buttons === 0) {
      drag.current = null; // the button came up somewhere we never heard about
      return;
    }
    if (!g.moved && Math.hypot(ev.clientX - g.x, ev.clientY - g.y) < 5) return;
    if (!g.moved) {
      g.moved = true;
      if (lastHover.current) {
        lastHover.current = null;
        onHover(null);
      }
    }
    if (g.rotate) {
      const c = wheelCenterScreen();
      if (!c) return;
      const a = Math.atan2(ev.clientY - c.y, ev.clientX - c.x);
      let da = a - g.lastA;
      if (da > Math.PI) da -= 2 * Math.PI;
      if (da < -Math.PI) da += 2 * Math.PI;
      const now = performance.now();
      g.v = ((da * 180) / Math.PI / Math.max(8, now - g.lastT)) * 16; // deg per frame
      g.lastA = a;
      g.lastT = now;
      setWheel(wheelRef.current + (da * 180) / Math.PI);
    } else {
      setPan({ x: g.pan0.x - (ev.clientX - g.x) * px, y: g.pan0.y - (ev.clientY - g.y) * px });
    }
  };
  const endPointer = (ev: ReactPointerEvent<SVGSVGElement>) => {
    pointers.current.delete(ev.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    const g = drag.current;
    if (!g || g.id !== ev.pointerId) return;
    drag.current = null;
    if (!g.moved) return;
    suppressClick.current = true;
    setTimeout(() => {
      suppressClick.current = false;
    }, 0);
    if (!g.rotate) return;
    // flick momentum (only if the finger was still moving), then a soft snap
    const idle = performance.now() - g.lastT > 90;
    let v = motion && !idle ? g.v : 0;
    const coast = () => {
      if (Math.abs(v) > 0.05) {
        v *= 0.93;
        setWheel((w) => w + v);
        spinRef.current = requestAnimationFrame(coast);
        return;
      }
      const w = wheelRef.current;
      const k = Math.round((180 - w) / SKY.step);
      const idx = (((k % STAGES.length) + STAGES.length) % STAGES.length) as number;
      bringToFront(idx);
    };
    spinRef.current = requestAnimationFrame(coast);
  };

  // ⌘/ctrl-scroll (and trackpad pinch, which arrives as ctrl+wheel) zooms around the pointer
  useEffect(() => {
    const svg = skyRef.current;
    if (!svg) return;
    const onWheel = (ev: WheelEvent) => {
      if (!(ev.ctrlKey || ev.metaKey) || diveStage) return;
      ev.preventDefault();
      zoomAtRef.current(Math.exp(-ev.deltaY * 0.0022), ev.clientX, ev.clientY);
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, [diveStage]);

  const fitAll = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  /**
   * What is under the pointer: the nearest job dot, stage (root badge or its
   * label) or the brain — by distance, never by paint order, so neighbours can't
   * steal each other's clicks and phone taps get a generous radius.
   */
  type Target = { kind: 'job'; id: string } | { kind: 'stage'; id: string } | { kind: 'hub' };
  const lastHover = useRef<string | null>(null);
  const [cursorKind, setCursorKind] = useState<'grab' | 'pointer'>('grab');
  const pickTarget = (clientX: number, clientY: number): Target | null => {
    const p = screenToWorld(clientX, clientY);
    if (!p) return null;
    let best: Target | null = null;
    let bestScore = 1; // distance / reach — anything under 1 is a hit
    const jobReach = Math.max(9, (coarse ? 16 : 11) * px);
    for (const [id, q] of jobPos) {
      const j = JOB_BY_ID.get(id);
      if (!j || filtered(j)) continue;
      const score = Math.hypot(q.x - p.x, q.y - p.y) / jobReach;
      if (score < bestScore) {
        bestScore = score;
        best = { kind: 'job', id };
      }
    }
    const rootReach = Math.max(30, (coarse ? 26 : 20) * px);
    for (const g of stageGeo) {
      const score = Math.hypot(g.root.x - p.x, g.root.y - p.y) / rootReach;
      if (score < bestScore) {
        bestScore = score;
        best = { kind: 'stage', id: g.t.stage.id };
      }
    }
    if (best) return best;
    for (const g of stageGeo) {
      // the label box, from its anchor
      const w = labelW(g);
      const x0 =
        g.anchor === 'start' ? g.label.x : g.anchor === 'end' ? g.label.x - w : g.label.x - w / 2;
      const fs = labelFs(labelName(g.t.stage.id, g.t.stage.name[locale]));
      if (
        p.x >= x0 - 8 * px &&
        p.x <= x0 + w + 8 * px &&
        p.y >= g.label.y - fs &&
        p.y <= g.label.y + fs
      )
        return { kind: 'stage', id: g.t.stage.id };
    }
    if (e < 0.5 && Math.hypot(p.x, p.y) < SKY.brain) return { kind: 'hub' };
    return null;
  };

  const openJob = (id: string, viaKeyboard = false) => {
    const j = JOB_BY_ID.get(id);
    if (!j) return;
    onDive(j.stage);
    onSelect(id, viaKeyboard);
  };

  /* keyboard: arrows turn the wheel / step stages, +/- zoom, 0 fits */
  const onKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
      ev.preventDefault();
      stepStage(ev.key === 'ArrowRight' ? 1 : -1);
    } else if ((ev.key === '+' || ev.key === '=') && !diveStage) {
      setZoom((z) => Math.min(4, z * 1.25));
    } else if (ev.key === '-' && !diveStage) {
      setZoom((z) => Math.max(0.6, z / 1.25));
    } else if (ev.key === '0' && !diveStage) {
      fitAll();
    }
  };
  const stepStage = (dir: 1 | -1) => {
    if (diveStage) {
      const i = STAGE_INDEX.get(diveStage) ?? 0;
      const next = STAGES[(i + dir + STAGES.length) % STAGES.length];
      if (next) {
        onSelect(null);
        onDive(next.id);
      }
      return;
    }
    if (mode === 'wheel') {
      const from = frontTarget.current ?? focusIndex;
      bringToFront((from + dir + STAGES.length) % STAGES.length);
    }
  };

  // Keyboard flow across scenes: entering a stage focuses its first job, leaving
  // focuses that stage's badge again — only when focus was ours to move.
  const prevDive = useRef(diveStage);
  useEffect(() => {
    const prev = prevDive.current;
    prevDive.current = diveStage;
    if (prev === diveStage) return;
    const root = rootRef.current;
    const ae = document.activeElement;
    if (ae && ae !== document.body && !root?.contains(ae)) return;
    // the dive scene mounts a render or two later — look for its first job briefly
    // (timers, not rAF: rAF pauses in background tabs and hidden panes)
    let tries = 0;
    let timer = 0;
    const seek = () => {
      const el = diveStage
        ? (root?.querySelector<HTMLElement | SVGElement>('[data-dive-first]') ??
          (tries > 8 ? root?.querySelector<HTMLElement>('[data-kart-back]') : null))
        : prev
          ? root?.querySelector<SVGElement>(`[data-stage="${prev}"]`)
          : null;
      if (el) el.focus({ preventScroll: true });
      else if (tries++ < 10) timer = window.setTimeout(seek, 30);
    };
    timer = window.setTimeout(seek, 0);
    return () => window.clearTimeout(timer);
  }, [diveStage]);

  /* ── render ── */
  const hoveredJob = hovered ? JOB_BY_ID.get(hovered) : undefined;
  const labelUnits = Math.min(narrow ? 52 : 46, Math.max(21, (narrow ? 10.5 : 15) * px));
  const subUnits = Math.min(30, Math.max(13, 11 * px));
  const shortLabels = box.w < 760;
  const labelName = (sid: string, full: string) =>
    shortLabels ? (STAGE_SHORT[sid]?.[locale] ?? full) : full;
  /** In the line layout a label must fit its column; on the wheel it has room. */
  const labelFs = (name: string) =>
    e > 0.5
      ? Math.min(labelUnits, (SKY.colW * 0.9) / (Math.max(4, name.length) * 0.78))
      : labelUnits;
  const labelW = (g: { t: StageTree }) => {
    const nm = labelName(g.t.stage.id, g.t.stage.name[locale]);
    return nm.length * labelFs(nm) * 0.78;
  };
  const skyLive = d < 0.5; // the sky owns focus and pointer only while no stage is open
  const reveal = (i: number) => ({ ['--d' as string]: `${0.15 + i * 0.07}s` });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: arrow/zoom shortcuts for the whole canvas; every target inside is a real control
    <div
      ref={rootRef}
      className="kart-sky rounded-[inherit] select-none"
      style={{ height: 'min(82vh, 900px)', minHeight: 460 }}
      onKeyDown={onKeyDown}
    >
      <Stars />

      {/* ── SKY ── */}
      <svg
        ref={skyRef}
        className="absolute inset-0 size-full"
        viewBox={`${f(skyView.x)} ${f(skyView.y)} ${f(skyView.w)} ${f(skyView.h)}`}
        aria-label={m.mapLabel}
        aria-hidden={skyLive ? undefined : true}
        style={{
          opacity: 1 - d,
          pointerEvents: skyLive ? undefined : 'none',
          cursor: drag.current?.moved ? 'grabbing' : cursorKind,
          touchAction: 'pan-y', // one finger up/down still scrolls the page; sideways turns the wheel
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onPointerLeave={() => {
          if (lastHover.current) {
            lastHover.current = null;
            onHover(null);
          }
        }}
        onClick={(ev) => {
          if (suppressClick.current) return;
          const t = pickTarget(ev.clientX, ev.clientY);
          if (t?.kind === 'job') openJob(t.id);
          else if (t?.kind === 'stage') onDive(t.id);
          else if (t?.kind === 'hub') onSelect('hub');
          else if (selected) onSelect(null);
        }}
      >
        {/* instrument rings + spokes (wheel only) */}
        <g opacity={(1 - e) * 0.9} pointerEvents="none">
          {[205, 262].map((r) => (
            <circle
              key={r}
              r={r}
              fill="none"
              stroke="var(--sky-faint)"
              strokeOpacity={0.35}
              strokeDasharray="2 8"
            />
          ))}
          {stageGeo.map((g) => {
            const a = polar(SKY.brain + 18, g.ang);
            const b = polar(SKY.ring - 26, g.ang);
            return (
              <line
                key={g.t.stage.id}
                x1={f(a.x)}
                y1={f(a.y)}
                x2={f(b.x)}
                y2={f(b.y)}
                stroke="var(--sky-faint)"
                strokeDasharray="2 6"
                strokeWidth={1.2}
              />
            );
          })}
        </g>

        {/* the brain */}
        <g opacity={1 - e}>
          <Brain spin={motion} />
          {/* biome-ignore lint/a11y/useSemanticElements: an SVG group cannot be a <button> */}
          <g
            role="button"
            tabIndex={skyLive && e < 0.5 ? 0 : -1}
            aria-label={m.hubName}
            className="group outline-none"
            pointerEvents="none"
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onSelect('hub', true);
              }
            }}
          >
            <circle
              className="kart-focus"
              r={SKY.brain + 16}
              fill="none"
              stroke="var(--sky-up)"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
            />
            {selected === 'hub' && (
              <circle
                r={SKY.brain + 16}
                fill="none"
                stroke="var(--sky-ink)"
                strokeWidth={1.5}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </g>
        </g>

        {/* constellations: real in-stage dependency branches */}
        {stageGeo.map((g, gi) => {
          const sid = g.t.stage.id;
          const c = STAGE_COLOR[sid] ?? '#fff';
          return (
            <g key={sid} style={{ ['--c' as string]: c }}>
              {[...g.t.nodes.values()].map((n) => {
                const to = jobPos.get(n.id);
                const from = n.parent ? jobPos.get(n.parent) : g.root;
                if (!to || !from) return null;
                const dim =
                  filtered(n.job) ||
                  (active && n.id !== active && !up.has(n.id) && !down.has(n.id));
                return (
                  <line
                    key={n.id}
                    x1={f(from.x)}
                    y1={f(from.y)}
                    x2={f(to.x)}
                    y2={f(to.y)}
                    pathLength={1}
                    className={cn('kart-draw', !n.parent && 'acc')}
                    style={reveal(gi)}
                    stroke={n.parent ? 'var(--sky-line)' : 'currentColor'}
                    strokeOpacity={n.parent ? 1 : 0.75}
                    strokeWidth={2}
                    opacity={dim ? 0.25 : 1}
                  />
                );
              })}
            </g>
          );
        })}

        {/* cross-stage dependency arcs for the active job */}
        {active && JOB_BY_ID.has(active) && (
          <g fill="none" pointerEvents="none">
            {JOBS.flatMap((j) =>
              j.deps.map((dep) => {
                const isUp = up.has(dep) && (j.id === active || up.has(j.id));
                const isDown = dep === active && down.has(j.id);
                if (!isUp && !isDown) return null;
                const a = jobPos.get(dep);
                const b = jobPos.get(j.id);
                if (!a || !b) return null;
                const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
                const ctrl = lerpPt(
                  { x: mid.x * 0.3, y: mid.y * 0.3 },
                  { x: mid.x, y: mid.y - 80 },
                  e,
                );
                return (
                  <path
                    key={`${dep}>${j.id}`}
                    d={`M${f(a.x)} ${f(a.y)}Q${f(ctrl.x)} ${f(ctrl.y)} ${f(b.x)} ${f(b.y)}`}
                    stroke={isUp ? 'var(--sky-up)' : 'var(--sky-down)'}
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                    opacity={0.9}
                    className="kart-fade"
                  />
                );
              }),
            )}
          </g>
        )}

        {/* stage roots (the buttons) + labels */}
        {stageGeo.map((g, gi) => {
          const sid = g.t.stage.id;
          const c = STAGE_COLOR[sid] ?? '#fff';
          const Icon = STAGE_ICON[sid] ?? Megaphone;
          const isFront = mode === 'wheel' && g.t.index === focusIndex && e < 0.5;
          const name = labelName(sid, g.t.stage.name[locale]);
          const sub = `${m.jobsWord(g.t.nodes.size)}${g.t.counts.missing ? ` · ${m.gapsWord(g.t.counts.missing)}` : ''}`;
          return (
            // biome-ignore lint/a11y/useSemanticElements: an SVG group cannot be a <button>
            <g
              key={sid}
              data-stage={sid}
              role="button"
              tabIndex={skyLive ? 0 : -1}
              aria-label={m.openStage(
                g.t.stage.name[locale],
                m.jobsWord(g.t.nodes.size),
                m.gapsWord(g.t.counts.missing),
              )}
              className="group outline-none"
              pointerEvents="none"
              style={{ ['--c' as string]: c }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onDive(sid);
                }
              }}
            >
              <g className="kart-pop" style={reveal(gi)}>
                <circle
                  cx={f(g.root.x)}
                  cy={f(g.root.y)}
                  r={26}
                  className="acc"
                  fill="currentColor"
                  fillOpacity={isFront ? 0.16 : 0.08}
                />
                <circle
                  cx={f(g.root.x)}
                  cy={f(g.root.y)}
                  r={18}
                  fill="var(--sky-bg-1)"
                  className="acc"
                  stroke="currentColor"
                  strokeOpacity={0.7}
                  strokeWidth={1.4}
                />
                <Icon
                  x={g.root.x - 8.5}
                  y={g.root.y - 8.5}
                  width={17}
                  height={17}
                  strokeWidth={1.8}
                  className="acc"
                  color="currentColor"
                  aria-hidden
                />
                <circle
                  className="kart-focus"
                  cx={f(g.root.x)}
                  cy={f(g.root.y)}
                  r={31}
                  fill="none"
                  stroke="var(--sky-up)"
                  strokeWidth={2.5}
                  vectorEffect="non-scaling-stroke"
                />
              </g>
              <text
                x={f(g.label.x)}
                y={f(g.label.y)}
                textAnchor={g.anchor}
                className="kart-serif"
                fontSize={f(labelFs(name))}
                letterSpacing="0.2em"
                fill="var(--sky-ink)"
                opacity={isFront ? 1 : 0.82}
              >
                {name.toUpperCase()}
              </text>
              {!narrow && (
                <text
                  x={f(g.label.x)}
                  y={f(g.label.y + subUnits * 1.6)}
                  textAnchor={g.anchor}
                  fontSize={f(subUnits)}
                  letterSpacing="0.06em"
                  fill={g.t.counts.missing ? 'var(--sky-bad)' : 'var(--sky-dim)'}
                  opacity={0.9}
                >
                  {sub}
                </text>
              )}
            </g>
          );
        })}

        {/* job dots */}
        <g>
          {stageGeo.map((g, gi) =>
            [...g.t.nodes.values()].map((n) => {
              const p = jobPos.get(n.id);
              if (!p) return null;
              const dim =
                filtered(n.job) || (active && n.id !== active && !up.has(n.id) && !down.has(n.id));
              const rank = RANK.get(n.id);
              const isSel = selected === n.id;
              return (
                <g key={n.id} opacity={dim ? 0.18 : 1} pointerEvents="none">
                  {rank !== undefined && (
                    <circle
                      cx={f(p.x)}
                      cy={f(p.y)}
                      r={11.5}
                      fill="none"
                      stroke="var(--sky-copper)"
                      strokeWidth={1.4}
                      className="kart-breathe"
                    />
                  )}
                  <g className="kart-pop" style={reveal(gi + 2)}>
                    <Glyph level={n.job.level} cx={p.x} cy={p.y} r={7.5} glow />
                  </g>
                  {(isSel || active === n.id) && (
                    <circle
                      cx={f(p.x)}
                      cy={f(p.y)}
                      r={13}
                      fill="none"
                      stroke="var(--sky-ink)"
                      strokeWidth={1.3}
                    />
                  )}
                </g>
              );
            }),
          )}
        </g>

        {/* tooltip for the hovered dot */}
        {hoveredJob &&
          jobPos.get(hoveredJob.id) &&
          (() => {
            const p = jobPos.get(hoveredJob.id) as Pt;
            const fs = 12.5 * px;
            const w =
              Math.max(hoveredJob.name[locale].length, m.level[hoveredJob.level].length) *
                fs *
                0.56 +
              18 * px;
            const h = fs * 3.1;
            const x = p.x + 14 * px;
            const y = p.y - h - 10 * px;
            return (
              <g pointerEvents="none" className="kart-fade">
                <rect
                  x={f(x)}
                  y={f(y)}
                  width={f(w)}
                  height={f(h)}
                  rx={f(6 * px)}
                  fill="var(--sky-bg-1)"
                  stroke="var(--sky-faint)"
                  strokeWidth={px}
                />
                <text
                  x={f(x + 9 * px)}
                  y={f(y + fs * 1.3)}
                  fontSize={f(fs)}
                  fill="var(--sky-ink)"
                  fontWeight={600}
                >
                  {hoveredJob.name[locale]}
                </text>
                <text
                  x={f(x + 9 * px)}
                  y={f(y + fs * 2.5)}
                  fontSize={f(fs * 0.86)}
                  fill={hoveredJob.level === 'missing' ? 'var(--sky-bad)' : 'var(--sky-dim)'}
                >
                  {m.level[hoveredJob.level]}
                </text>
              </g>
            );
          })()}
      </svg>

      {/* ── DIVE ── */}
      {shownDive && (
        <DiveScene
          tree={TREE_BY_STAGE.get(shownDive)}
          locale={locale}
          m={m}
          d={d}
          box={box}
          reserve={reserve}
          narrow={narrow}
          selected={selected}
          hovered={hovered}
          levelFilter={levelFilter}
          onHover={onHover}
          onSelect={onSelect}
        />
      )}

      {/* ── toolbar ── */}
      <div className="absolute left-3 top-3 z-10 flex items-center gap-2">
        {diveStage ? (
          <button
            type="button"
            data-kart-back
            onClick={() => {
              onSelect(null);
              onDive(null);
            }}
            className="flex h-9 items-center gap-1.5 rounded-md border border-[var(--sky-faint)] bg-[var(--sky-bg-1)]/80 px-3 text-label text-[var(--sky-ink)] backdrop-blur transition-fast hover:bg-[var(--sky-bg-0)] sm:h-8"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            {m.back}
          </button>
        ) : (
          <>
            <div className="flex rounded-md border border-[var(--sky-faint)] bg-[var(--sky-bg-1)]/80 p-0.5 backdrop-blur">
              {(
                [
                  ['wheel', m.wheel, Orbit],
                  ['line', m.journey, Waypoints],
                ] as const
              ).map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={mode === id}
                  onClick={() => {
                    onMode(id);
                    fitAll();
                  }}
                  className={cn(
                    'flex h-9 items-center gap-1.5 rounded px-3 text-label tracking-wide transition-fast sm:h-7',
                    mode === id
                      ? 'bg-[var(--sky-ink)] font-medium text-[var(--sky-bg-1)]'
                      : 'text-[var(--sky-dim)] hover:text-[var(--sky-ink)]',
                  )}
                >
                  <Icon className="size-3.5" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
            <div className="hidden overflow-hidden rounded-md border border-[var(--sky-faint)] bg-[var(--sky-bg-1)]/80 backdrop-blur sm:flex">
              {(
                [
                  [m.zoomOut, Minus, () => setZoom((z) => Math.max(0.6, z / 1.3))],
                  [m.zoomIn, Plus, () => setZoom((z) => Math.min(4, z * 1.3))],
                  [m.fit, Maximize2, fitAll],
                ] as const
              ).map(([label, Icon, run]) => (
                <button
                  key={label}
                  type="button"
                  aria-label={label}
                  title={label}
                  onClick={run}
                  className="flex size-7 items-center justify-center text-[var(--sky-dim)] transition-fast hover:bg-[var(--sky-bg-0)] hover:text-[var(--sky-ink)]"
                >
                  <Icon className="size-3.5" aria-hidden />
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* stage chevrons (wheel and dive) */}
      {((diveStage && !narrow) || (!diveStage && mode === 'wheel')) && (
        <>
          <button
            type="button"
            aria-label={m.prevStage}
            onClick={() => stepStage(-1)}
            className="absolute left-2 top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-[var(--sky-dim)] transition-fast hover:bg-[var(--sky-ink)]/10 hover:text-[var(--sky-ink)]"
          >
            <ChevronLeft className="size-6" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={m.nextStage}
            onClick={() => stepStage(1)}
            className="absolute top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full text-[var(--sky-dim)] transition-fast hover:bg-[var(--sky-ink)]/10 hover:text-[var(--sky-ink)]"
            style={{ right: 8 + reserve }}
          >
            <ChevronRight className="size-6" aria-hidden />
          </button>
        </>
      )}

      <p
        className="pointer-events-none absolute bottom-3 left-4 z-10 max-w-md text-micro tracking-wide text-[var(--sky-dim)]"
        style={{ right: 16 + reserve }}
      >
        {diveStage
          ? coarse
            ? m.diveHintTouch
            : m.diveHint
          : coarse
            ? m.skyHintTouch
            : mode === 'line'
              ? m.skyHintLine
              : m.skyHint}
      </p>
    </div>
  );
}

/* ── the dive scene ──────────────────────────────────────────────────── */

function wrapWords(text: string, max: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    if (cur && `${cur} ${w}`.length > max) {
      lines.push(cur);
      cur = w;
    } else cur = cur ? `${cur} ${w}` : w;
  }
  if (cur) lines.push(cur);
  return lines.slice(0, 3);
}

function DiveScene({
  tree,
  locale,
  m,
  d,
  box,
  reserve,
  narrow,
  selected,
  hovered,
  levelFilter,
  onHover,
  onSelect,
}: {
  tree: StageTree | undefined;
  locale: Locale;
  m: SkyMessages;
  d: number;
  box: { w: number; h: number };
  reserve: number;
  narrow: boolean;
  selected: string | null;
  hovered: string | null;
  levelFilter: Level | null;
  onHover: (id: string | null) => void;
  onSelect: (id: string | null, viaKeyboard?: boolean) => void;
}) {
  if (!tree) return null;
  const sid = tree.stage.id;
  const c = STAGE_COLOR[sid] ?? '#fff';
  const Icon = STAGE_ICON[sid] ?? Megaphone;
  const name = tree.stage.name[locale];
  const sub = [
    m.jobsWord(tree.nodes.size),
    tree.counts.missing ? m.gapsWord(tree.counts.missing) : null,
    m.stageOf(tree.index + 1, STAGES.length),
  ]
    .filter(Boolean)
    .join(' · ');

  // in-stage chain highlight
  const active =
    hovered && tree.nodes.has(hovered)
      ? hovered
      : selected && tree.nodes.has(selected)
        ? selected
        : null;
  const chain = new Set<string>();
  if (active) {
    let p: string | null | undefined = active;
    while (p) {
      chain.add(p);
      p = tree.nodes.get(p)?.parent;
    }
    for (const ch of tree.nodes.get(active)?.children ?? []) chain.add(ch);
  }

  const style = {
    ['--c' as string]: c,
    opacity: d,
    pointerEvents: d > 0.5 ? ('auto' as const) : ('none' as const),
  };

  if (narrow)
    return (
      <DiveList
        tree={tree}
        locale={locale}
        m={m}
        style={style}
        selected={selected}
        levelFilter={levelFilter}
        onSelect={onSelect}
        sub={sub}
      />
    );

  const local = FAN_LOCAL.get(sid) ?? new Map<string, Pt>();
  const b = fanBounds(sid);
  const content: View = { x: b.x0, y: b.y0, w: b.x1 - b.x0, h: b.y1 - b.y0 };
  const { view, s: fanS } = fitView(content, box, reserve);
  // text is sized for the screen, not the world: never below the 11px floor
  const T = (pxSize: number, min: number, max: number) =>
    Math.min(max, Math.max(min, pxSize / fanS));
  const nameFs = T(13, 17, 26);
  const levelFs = T(11, 13, 20);
  const tagFs = T(11, 12.5, 18);
  const rankFs = T(11, 13, 18);
  const lineH = nameFs * 1.18;
  const firstId = tree.roots[0];
  const k = 0.9 + 0.1 * d; // a little growth as it arrives

  return (
    <svg
      className="absolute inset-0 size-full"
      viewBox={`${f(view.x)} ${f(view.y)} ${f(view.w)} ${f(view.h)}`}
      style={style}
      aria-label={name}
      aria-hidden={d > 0.5 ? undefined : true}
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && selected) onSelect(null);
      }}
    >
      <g transform={`scale(${k.toFixed(3)})`}>
        {/* ghost watermark */}
        <text
          x={0}
          y={f((b.y0 + 40) / 1.6)}
          textAnchor="middle"
          className="kart-serif"
          fontSize={210}
          letterSpacing="0.06em"
          fill="var(--sky-ink)"
          opacity={0.045}
          pointerEvents="none"
        >
          {name.toUpperCase()}
        </text>

        {/* branches */}
        {[...tree.nodes.values()].map((n) => {
          const to = local.get(n.id);
          const from = n.parent ? local.get(n.parent) : { x: 0, y: 0 };
          if (!to || !from) return null;
          const lit = active ? chain.has(n.id) && (!n.parent || chain.has(n.parent)) : false;
          return (
            <line
              key={n.id}
              x1={f(from.x)}
              y1={f(from.y)}
              x2={f(to.x)}
              y2={f(to.y)}
              pathLength={1}
              className={cn('kart-draw', !n.parent && 'acc')}
              style={{ ['--d' as string]: `${0.1 + n.depth * 0.12}s` }}
              stroke={lit ? 'var(--sky-ink)' : n.parent ? 'var(--sky-line)' : 'currentColor'}
              strokeOpacity={n.parent || lit ? 1 : 0.7}
              strokeWidth={lit ? 3 : 2}
              opacity={active && !lit ? 0.45 : 1}
            />
          );
        })}

        {/* root badge + stage name */}
        <g pointerEvents="none">
          <circle r={FAN.root + 12} className="acc" fill="currentColor" fillOpacity={0.1} />
          <circle
            r={FAN.root}
            fill="var(--sky-bg-1)"
            className="acc"
            stroke="currentColor"
            strokeWidth={2}
          />
          <Icon
            x={-20}
            y={-20}
            width={40}
            height={40}
            strokeWidth={1.6}
            className="acc"
            color="currentColor"
            aria-hidden
          />
          <text
            y={FAN.root + 58}
            textAnchor="middle"
            className="kart-serif"
            fontSize={40}
            letterSpacing="0.2em"
            fill="var(--sky-ink)"
          >
            {name.toUpperCase()}
          </text>
          <text
            y={FAN.root + 92}
            textAnchor="middle"
            fontSize={f(T(12, 15, 24))}
            letterSpacing="0.06em"
            fill="var(--sky-dim)"
          >
            {sub}
          </text>
        </g>

        {/* job nodes */}
        {[...tree.nodes.values()].map((n) => {
          const p = local.get(n.id);
          if (!p) return null;
          const j = n.job;
          const rank = RANK.get(n.id);
          const isSel = selected === n.id;
          const dim =
            Boolean(levelFilter && j.level !== levelFilter) ||
            (active !== null && !chain.has(n.id));
          const lines = wrapWords(j.name[locale], 16);
          return (
            // biome-ignore lint/a11y/useSemanticElements: an SVG group cannot be a <button>
            <g
              key={n.id}
              data-dive-first={n.id === firstId ? '' : undefined}
              role="button"
              tabIndex={d > 0.5 ? 0 : -1}
              aria-label={`${j.name[locale]} – ${m.level[j.level]}`}
              aria-pressed={isSel}
              className="group cursor-pointer outline-none focus-visible:opacity-100"
              opacity={dim ? 0.3 : 1}
              onPointerEnter={() => onHover(n.id)}
              onPointerLeave={() => onHover(null)}
              onFocus={() => onHover(n.id)}
              onBlur={() => onHover(null)}
              onClick={(ev) => {
                ev.stopPropagation();
                onSelect(isSel ? null : n.id);
              }}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onSelect(n.id, true);
                }
              }}
            >
              <circle cx={f(p.x)} cy={f(p.y)} r={FAN.node + 14} fill="transparent" />
              <g className="kart-pop" style={{ ['--d' as string]: `${0.2 + n.depth * 0.12}s` }}>
                <Glyph level={j.level} cx={p.x} cy={p.y} r={FAN.node} glow />
              </g>
              <circle
                className="kart-focus"
                cx={f(p.x)}
                cy={f(p.y)}
                r={FAN.node + 9}
                fill="none"
                stroke="var(--sky-up)"
                strokeWidth={2.5}
                vectorEffect="non-scaling-stroke"
              />
              {(isSel || hovered === n.id) && (
                <circle
                  cx={f(p.x)}
                  cy={f(p.y)}
                  r={FAN.node + 7}
                  fill="none"
                  stroke="var(--sky-ink)"
                  strokeWidth={isSel ? 2.4 : 1.2}
                />
              )}
              {rank !== undefined && (
                <g>
                  <circle
                    cx={f(p.x + 23)}
                    cy={f(p.y - 23)}
                    r={f(Math.max(12, rankFs * 0.95))}
                    fill="var(--sky-copper)"
                  />
                  <text
                    x={f(p.x + 23)}
                    y={f(p.y - 23)}
                    dy="0.35em"
                    textAnchor="middle"
                    fontSize={f(rankFs)}
                    fontWeight={700}
                    fill="var(--sky-bg-1)"
                  >
                    {rank + 1}
                  </text>
                </g>
              )}
              {rank === 0 && (
                <text
                  x={f(p.x)}
                  y={f(p.y - FAN.node - 16 - tagFs * 0.6)}
                  textAnchor="middle"
                  fontSize={f(tagFs)}
                  fontWeight={700}
                  letterSpacing="0.14em"
                  fill="var(--sky-copper)"
                >
                  {m.buildFirst}
                </text>
              )}
              {lines.map((ln, li) => (
                <text
                  key={ln}
                  x={f(p.x)}
                  y={f(p.y + FAN.node + 8 + nameFs + li * lineH)}
                  textAnchor="middle"
                  fontSize={f(nameFs)}
                  fontWeight={isSel ? 650 : 500}
                  fill="var(--sky-ink)"
                >
                  {ln}
                </text>
              ))}
              <text
                x={f(p.x)}
                y={f(p.y + FAN.node + 10 + nameFs + lines.length * lineH)}
                textAnchor="middle"
                fontSize={f(levelFs)}
                letterSpacing="0.04em"
                fill={j.level === 'missing' ? 'var(--sky-bad)' : 'var(--sky-dim)'}
              >
                {m.level[j.level]}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** Narrow screens: the stage's tree as an indented list (same glyphs). */
function DiveList({
  tree,
  locale,
  m,
  style,
  selected,
  levelFilter,
  onSelect,
  sub,
}: {
  tree: StageTree;
  locale: Locale;
  m: SkyMessages;
  style: React.CSSProperties;
  selected: string | null;
  levelFilter: Level | null;
  onSelect: (id: string | null, viaKeyboard?: boolean) => void;
  sub: string;
}) {
  const Icon = STAGE_ICON[tree.stage.id] ?? Megaphone;
  const rows: { id: string; depth: number }[] = [];
  const walk = (ids: string[], depth: number) => {
    for (const id of ids) {
      rows.push({ id, depth });
      walk(tree.nodes.get(id)?.children ?? [], depth + 1);
    }
  };
  walk(tree.roots, 0);
  return (
    <div className="absolute inset-0 overflow-y-auto px-4 pb-10 pt-16" style={style}>
      <div className="mb-4 flex items-center gap-3">
        <span className="acc flex size-11 items-center justify-center rounded-full border-2 border-current">
          <Icon className="size-5" aria-hidden />
        </span>
        <div>
          <div className="kart-serif text-section tracking-[0.18em] text-[var(--sky-ink)]">
            {tree.stage.name[locale].toUpperCase()}
          </div>
          <div className="text-micro text-[var(--sky-dim)]">{sub}</div>
        </div>
      </div>
      <ul className="flex flex-col">
        {rows.map(({ id, depth }) => {
          const j = JOB_BY_ID.get(id);
          if (!j) return null;
          const rank = RANK.get(id);
          return (
            <li key={id} style={{ paddingLeft: depth * 18 }}>
              <button
                type="button"
                data-dive-first={id === tree.roots[0] ? '' : undefined}
                onClick={() => onSelect(selected === id ? null : id)}
                className={cn(
                  'flex min-h-11 w-full items-center gap-3 rounded-md px-2 py-1.5 text-left transition-fast',
                  depth > 0 && 'border-l border-[var(--sky-faint)]',
                  selected === id ? 'bg-[var(--sky-ink)]/10' : 'hover:bg-[var(--sky-ink)]/5',
                  levelFilter && j.level !== levelFilter && 'opacity-35',
                )}
              >
                <svg viewBox="-12 -12 24 24" className="size-6 shrink-0" aria-hidden>
                  <Glyph level={j.level} cx={0} cy={0} r={8} />
                </svg>
                <span className="min-w-0 flex-1">
                  <span className="block text-body text-[var(--sky-ink)]">{j.name[locale]}</span>
                  <span
                    className={cn(
                      'block text-micro',
                      j.level === 'missing' ? 'text-[var(--sky-bad)]' : 'text-[var(--sky-dim)]',
                    )}
                  >
                    {m.level[j.level]}
                  </span>
                </span>
                {rank !== undefined && (
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--sky-copper)] text-micro font-bold text-[var(--sky-bg-1)]">
                    {rank + 1}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
